module Meevo.Plugins.MeevoStore {
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import TrackingItem = Meevo.Model.TrackingItem;
    import ModalTranslationModes = Meevo.Components.Modal.ModalTranslationModes;

    export interface IStoreEdition {
        basePrice: number;
        description: string;
        displayName: string;
        editionPlanId: string;
        includedText: string;
        maxUserAmount: number;
        salesForceIdentifier: string;
        sequenceNumber: number;
        sfEditionId: string;
        smsPlanId: string;
        supportPlanId: string,
        includes?: Array<string>;
        salesForceReferenceNumber?: number;
        entityId?: string; // editionPlanId when in pending object
    }

    export interface IMessageOption {
        displayName: string;
        msrp: number;
        overagePrice: number;
        salesForceIdentifier: string;
        sequenceNumber: number;
        sfEditionId: string;
        smsPlanId: string;
        price?: number;
        salesForceReferenceNumber?: number;
        entityId?: string; // editionPlanId when in pending object
    }
    export interface ISupportPlan {
        supportPlanId: string;
        salesForceIdentifier: string;
        sequenceNumber: number;
        displayName: string;
        description: string;
        descriptions: Array<string>;
        price?: number;
        entityId?: string; // editionPlanId when in pending object
    }

    export interface IStoreAccountInfo {
        accessLevel?: AccessLevelEnum;
        currentEditionId: string;
        currentMessageId: string;
        currentSupportPlanId: string;
        meevoEditions: Array<IStoreEdition>;
        messageOptions: Array<IMessageOption>;
        supportPlans: Array<ISupportPlan>;
        numberOfMessagesUsed?: number;

        lastTimeMeevoPlanChanged?: Date;
        lastTimeMeevoPlanChangedCountDays: number;
        lastTimeMessagingPlanChanged?: Date;
        lastTimeMessagingPlanChangedCountDays: number;
        lastTimeSupportPlanChanged?: Date;
        lastTimeSupportPlanChangedCountDays: number;

        editionPlanPending: IStoreEdition;
        messagePlanPending: IMessageOption;
        supportPlanPending: ISupportPlan;

        messagePlanUpgrades: Array<any>;
        supportPlanUpgrades: Array<any>;

        maxNumberOfEmployees?: number;
        pendingRequests?: Array<any>;
    }

    export enum StoreActionTypeEnum {
        Add = 30382,
        Remove = 30383
    }

    export enum AccessLevelEnum {
        Admin = 11045325,
        Read_Only = 11045326,
        No_Access = 11045327,
    }

    export enum ModuleDocumentsTypeEnum {
        ModulePrivacyPolicy = 30461,
        ModuleTermsAndConditions = 30460
    }

    export enum SystemDocumentsTypeEnum {
        MeevoStorePrivacyPolicy = 30459,
        MeevoStoreTermsAndConditions = 30458,
        MeevoStoreAdjustment = 20147,
    }

    export interface IMeevoStoreDocumentPolicy {
        documentsURL?: string,
        documentsText?: string,
    }

    export interface IXchangeOptions {
        hasXchangeSecurity: boolean,
        millenniumExchangeUri: string
    }

    export interface IMeevoStoreService {
        getAccountInfo(): angular.IPromise<IStoreAccountInfo>;
        getSaleItemList(itemType, currentEditionId): angular.IPromise<IMeevoStoreModuleType[]>;
        getTerms(type?, moduleId?): angular.IPromise<any>;
        openTermsModal(documentsText: string, modalTitle: string): angular.IPromise<any>;
        openConfirmModal(headerContentKey, confirmText, confirmItem): angular.IPromise<any>;
        openPurchaseModal(orderNumber: string, url?: string): angular.IPromise<any>;
        sendEmailNotification(moduleId: number, moduleType: SaleItemTypeEnum): angular.IPromise<any>;
        openMessageModal(contentKey: string): angular.IPromise<any>;
        getXchangeOptions(baseSecurityKey: string, linkType: LinkType): angular.IPromise<IXchangeOptions>;
    }

    export class MeevoStoreService implements IMeevoStoreService {

        static $inject = [
            '$q',
            'meevo.components.common.DataService',
            'meevo.AppSettings',
            'meevo.components.tracking.TrackingService',
            'meevo.components.modal.ModalService',
            '$translate',
            "meevo.components.common.SecurityService",
            "$sce"
        ];

        public routeMeevoStore: string;

        constructor(
            protected $q: angular.IQService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected trackingService: Meevo.Components.Tracking.ITrackingService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected $translate: angular.translate.ITranslateService,
            protected securityService: Meevo.Components.Common.ISecurityService,
            protected $sce: ng.ISCEService,
        ) {
            this.routeMeevoStore = this.appSettings.ROUTE_NAMES.meevoStore;
        }


        public getAccountInfo (): ng.IPromise<IStoreAccountInfo> {
            return this.dataService.customMethodData(this.appSettings.API_ROOT + '/' + this.routeMeevoStore + '/AccountInfo', 'GET', {})
                .then((response) => {
                    if (response && response.data && response.data.meevoEditions) {
                        response.data.meevoEditions.sort((a,b) => {
                            return a.sequenceNumber - b.sequenceNumber;
                        });
                        response.data.messageOptions.sort((a,b) => {
                            return a.sequenceNumber - b.sequenceNumber;
                        });
                    }
                    return response.data;
                });
        }

        public getSaleItemList (itemType, currentEditionId) {
            return this.dataService.customMethodData(this.appSettings.API_ROOT + '/' + this.appSettings.ROUTE_NAMES.meevoStore + '/SaleItem/Detail', 'POST', {'type': itemType, editionPlanId: currentEditionId});
        }

        public getTerms (type?: null, moduleId?: null): ng.IPromise<IMeevoStoreDocumentPolicy> {
            let isSystemType = false;
            let urlTerms = '/SaleItem/TermsAndConditions';
            let params = {
                type: type,
                moduleId: moduleId
            };

            if (type
                && type === SystemDocumentsTypeEnum.MeevoStorePrivacyPolicy
                || type === SystemDocumentsTypeEnum.MeevoStoreTermsAndConditions
                || type === SystemDocumentsTypeEnum.MeevoStoreAdjustment
            ) {
                isSystemType = true;
                urlTerms =  '/TermsAndConditions';
            }

            return this.dataService.customMethodData(this.appSettings.API_ROOT + '/' + this.appSettings.ROUTE_NAMES.meevoStore + urlTerms, 'GET', {}, params).then((response) => {
                let data = response.data[0];
                let document: IMeevoStoreDocumentPolicy = {
                    documentsURL: data.documentsURL,
                    documentsText: data.documentsText,
                };

                if (isSystemType && data) {
                    document = {
                        documentsURL: data.systemDocumentsURL,
                        documentsText: data.systemDocumentsText,
                    }
                }

                return document;
            });
        }

        public openTermsModal (documentsText: string, modalTitle: string = 'LABEL_TERMS_OF_SERVICE'): ng.IPromise<any> {

            // Initialize modal
            const options: Components.Modal.IModalOptions = {
                templateUrl: 'app/plugins/meevoStore/pages/modalTerms/modalTerms.html',
                clickOutsideToClose: false,
                bindToController: true,
                skipHide: true,
                locals: {
                    modalTitle: modalTitle,
                    modalText: documentsText
                },
                controller: 'meevo.plugins.meevoStore.ModalTermsController',
                controllerAs: 'vm'
            };
            return this.modalService.showCustom(options);
        }

        public openConfirmModal(headerContentKey: string, confirmText: string, confirmItem): ng.IPromise<any> {
            const options: Components.Modal.IModalOptions = {
                templateUrl: 'app/plugins/meevoStore/pages/modalProcess/modalProcess.html',
                clickOutsideToClose: false,
                locals: {
                    headerContentKey: headerContentKey,
                    confirmText: confirmText,
                    confirmItem: confirmItem
                },
                controller: 'meevo.plugins.meevoStore.ModalProcessController',
                controllerAs: 'vm'
            };
            return this.modalService.showCustom(options);
        }

        public openPurchaseModal(orderNumber: string, url: string): ng.IPromise<any> {
            const options: Components.Modal.IModalOptions = {
                templateUrl: 'app/plugins/meevoStore/pages/modalPurchase/modalPurchase.html',
                clickOutsideToClose: false,
                locals: {
                    modalTitle: 'LABEL_THANK_YOU_FOR_CHOOSING_MSI',
                    modalText: this.$translate.instant('TEXT_YOU_WILL_RECEIVE_EMAIL_NOTIFICATIONS'),
                    orderNumber: orderNumber,
                    url: url,
                },
                controller: 'meevo.plugins.meevoStore.ModalPurchaseController',
                controllerAs: 'vm'
            };
            return this.modalService.showCustom(options);
        }

        public openMessageModal(contentKey: string): ng.IPromise<any> {
            return this.modalService.message(contentKey)
        }

        public sendEmailNotification(moduleId: number, moduleType: SaleItemTypeEnum): angular.IPromise<any> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.ROUTE_NAMES.meevoStore}/SaleItem/EmailNotification`, 'POST', {
                moduleId,
                moduleType
            });
        }

        public getXchangeOptions(baseSecurityKey: string, linkType: Meevo.LinkType): angular.IPromise<IXchangeOptions> {
            let options: IXchangeOptions = {
                hasXchangeSecurity: false,
                millenniumExchangeUri: null
            };

            return this.securityService.checkModuleAccess(baseSecurityKey  + Meevo.Components.Common.SecurityParts.KEY_PART_READ).
                then((securityAccess: Meevo.Model.SecurityAccessEnum) => {

                    if (securityAccess == Meevo.Model.SecurityAccessEnum.Grant) {
                        options.hasXchangeSecurity = true;
                        return this.dataService.customMethodData(this.appSettings.API_ROOT + '/' + 'MeevoStore/CommunityLink', 'GET', null, { linkType }).then(data => {
                            if (data && data.data && data.data.errorMessage) {
                                options.hasXchangeSecurity = false;
                                return options;
                            }
                            options.millenniumExchangeUri = this.$sce.trustAsResourceUrl(data.data.link);
                            return options;
                        });
                    }
                    return options;
                });
        } 
    }

    angular.module('meevo.plugins.meevoStore')
        .service('meevo.plugins.meevoStore.MeevoStoreService', MeevoStoreService);
}
